import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ValidationUtil from '../../../util/ValidationUtil';
import { getPolyglot } from '../../../i18n';

interface PasswordRequirementsProps {
  password: string;
  passwordConfirm?: string;
}

const requirementKeys = [
  { key: 'minLength', label: 'error.formular.password_min_length' },
  { key: 'hasNumber', label: 'error.formular.password_number' },
  { key: 'hasSymbol', label: 'error.formular.password_symbol' },
  { key: 'hasLowercase', label: 'error.formular.password_lowercase' },
  { key: 'hasUppercase', label: 'error.formular.password_uppercase' },
] as const;

const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  passwordConfirm,
}) => {
  const polyglot = getPolyglot();
  const { rules } = ValidationUtil.validatePassword(password);

  if (!password) return null;

  const showConfirmFeedback =
    passwordConfirm !== undefined && passwordConfirm.length > 0;
  const passwordsMatch = password === passwordConfirm;

  return (
    <Box sx={{ mt: 1 }}>
      {requirementKeys.map(({ key, label }) => {
        const passed = rules[key];
        return (
          <Box
            key={key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              py: 0.25,
            }}
          >
            {passed ? (
              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
            )}
            <Typography
              variant="caption"
              sx={{ color: passed ? 'success.main' : 'error.main' }}
            >
              {polyglot.t(label)}
            </Typography>
          </Box>
        );
      })}
      {showConfirmFeedback && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            py: 0.25,
          }}
        >
          {passwordsMatch ? (
            <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
          ) : (
            <CancelIcon sx={{ fontSize: 16, color: 'error.main' }} />
          )}
          <Typography
            variant="caption"
            sx={{
              color: passwordsMatch ? 'success.main' : 'error.main',
            }}
          >
            {polyglot.t(
              passwordsMatch
                ? 'error.formular.passwords_match'
                : 'error.formular.passwords_no_match'
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PasswordRequirements;
